import { useSyncExternalStore } from 'react'

const SESSION_LOCK_NAME = 'webx-single-session'
const LEADER_STORAGE_KEY = 'webx-session-leader'
const BROADCAST_CHANNEL_NAME = 'webx-single-session'
const TAKEOVER_STORAGE_KEY = 'webx-session-takeover'
const TAB_ID_STORAGE_KEY = 'webx-tab-id'
const HEARTBEAT_INTERVAL_MS = 2000
const LEADER_STALE_MS = 5000
const BLOCKED_RETRY_MS = 3000
const RELEASE_LOCK_DELAY_MS = 150
const TAKEOVER_SETTLE_MS = 150

type SessionPhase = 'idle' | 'checking' | 'blocked' | 'active'

type SessionSnapshot = {
  isReady: boolean
  isBlocked: boolean
  supersededByTakeover: boolean
}

type SessionListener = () => void

const DISABLED_SNAPSHOT: SessionSnapshot = { isReady: true, isBlocked: false, supersededByTakeover: false }

function snapshotFromPhase(phase: SessionPhase): SessionSnapshot {
  switch (phase) {
    case 'checking':
      return { isReady: false, isBlocked: false, supersededByTakeover: false }
    case 'blocked':
      return { isReady: true, isBlocked: true, supersededByTakeover: false }
    case 'active':
      return { isReady: true, isBlocked: false, supersededByTakeover: false }
    default:
      return { isReady: false, isBlocked: false, supersededByTakeover: false }
  }
}

function snapshotsEqual(a: SessionSnapshot, b: SessionSnapshot): boolean {
  return (
    a.isReady === b.isReady &&
    a.isBlocked === b.isBlocked &&
    a.supersededByTakeover === b.supersededByTakeover
  )
}

function supportsWebLocks(): boolean {
  return typeof navigator !== 'undefined' && 'locks' in navigator
}

function getOrCreateTabId(): string {
  let tabId = sessionStorage.getItem(TAB_ID_STORAGE_KEY)
  if (!tabId) {
    tabId = crypto.randomUUID()
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId)
  }
  return tabId
}

let broadcastChannel: BroadcastChannel | null = null

function getBroadcastChannel(): BroadcastChannel {
  broadcastChannel ??= new BroadcastChannel(BROADCAST_CHANNEL_NAME)
  return broadcastChannel
}

function createWebLocksCoordinator() {
  let phase: SessionPhase = 'idle'
  let snapshot: SessionSnapshot = snapshotFromPhase('idle')
  let subscriberCount = 0
  let releaseHold: (() => void) | null = null
  let holdsLock = false
  let lockRequestInFlight = false
  let retryTimer: ReturnType<typeof setInterval> | null = null
  let releaseDelayTimer: ReturnType<typeof setTimeout> | null = null
  let suppressLockReacquire = false
  let supersededByTakeover = false
  const listeners = new Set<SessionListener>()

  const buildSnapshot = (nextPhase: SessionPhase): SessionSnapshot => ({
    ...snapshotFromPhase(nextPhase),
    supersededByTakeover: supersededByTakeover && nextPhase === 'blocked',
  })

  const emit = () => listeners.forEach((listener) => listener())

  const applyPhase = (next: SessionPhase) => {
    phase = next
    const nextSnapshot = buildSnapshot(next)
    if (snapshotsEqual(snapshot, nextSnapshot)) return
    snapshot = nextSnapshot
    emit()
  }

  const setHolder = () => {
    holdsLock = true
    if (retryTimer) {
      clearInterval(retryTimer)
      retryTimer = null
    }
    applyPhase('active')
  }

  const setBlocked = () => {
    applyPhase('blocked')
    startBlockedRetry()
  }

  const setChecking = () => {
    if (phase === 'blocked' || phase === 'active') return
    applyPhase('checking')
  }

  const releaseLock = () => {
    releaseHold?.()
    releaseHold = null
    holdsLock = false
  }

  const tryAcquireLock = (options: { updateUi: boolean }) => {
    if (lockRequestInFlight || holdsLock || subscriberCount === 0) return

    lockRequestInFlight = true
    if (options.updateUi) {
      setChecking()
    }

    void navigator.locks.request(
      SESSION_LOCK_NAME,
      { mode: 'exclusive', ifAvailable: true },
      async (lock) => {
        lockRequestInFlight = false
        if (subscriberCount === 0) return

        if (!lock) {
          setBlocked()
          return
        }

        setHolder()
        await new Promise<void>((resolve) => {
          releaseHold = resolve
        })
        holdsLock = false
        lockRequestInFlight = false

        if (subscriberCount > 0 && !suppressLockReacquire) {
          tryAcquireLock({ updateUi: false })
        }
      }
    )
  }

  const startBlockedRetry = () => {
    if (retryTimer || subscriberCount === 0) return
    retryTimer = setInterval(() => tryAcquireLock({ updateUi: false }), BLOCKED_RETRY_MS)
  }

  const handleTakeoverFromOtherTab = () => {
    supersededByTakeover = true
    suppressLockReacquire = true
    if (retryTimer) {
      clearInterval(retryTimer)
      retryTimer = null
    }
    releaseLock()
    if (subscriberCount > 0) {
      phase = 'blocked'
      const nextSnapshot = buildSnapshot('blocked')
      if (!snapshotsEqual(snapshot, nextSnapshot)) {
        snapshot = nextSnapshot
        emit()
      }
    }
  }

  const channel = getBroadcastChannel()
  channel.addEventListener('message', (event: MessageEvent<{ type: string }>) => {
    if (event.data?.type === 'takeover') {
      handleTakeoverFromOtherTab()
    }
  })

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === TAKEOVER_STORAGE_KEY) {
      handleTakeoverFromOtherTab()
    }
  })

  return {
    getSnapshot: () => snapshot,
    subscribe(listener: SessionListener) {
      listeners.add(listener)
      subscriberCount += 1
      suppressLockReacquire = false

      if (releaseDelayTimer) {
        clearTimeout(releaseDelayTimer)
        releaseDelayTimer = null
      }

      if (holdsLock) {
        applyPhase('active')
      } else if (phase === 'blocked') {
        startBlockedRetry()
      } else {
        tryAcquireLock({ updateUi: true })
      }

      listener()

      return () => {
        listeners.delete(listener)
        subscriberCount -= 1

        if (subscriberCount === 0) {
          if (retryTimer) {
            clearInterval(retryTimer)
            retryTimer = null
          }
          releaseDelayTimer = setTimeout(() => {
            releaseDelayTimer = null
            if (subscriberCount === 0) {
              releaseLock()
              phase = 'idle'
              snapshot = snapshotFromPhase('idle')
              supersededByTakeover = false
            }
          }, RELEASE_LOCK_DELAY_MS)
        }
      }
    },
    async requestTakeover() {
      localStorage.setItem(TAKEOVER_STORAGE_KEY, String(Date.now()))
      channel.postMessage({ type: 'takeover' })
      releaseLock()
      await new Promise((resolve) => setTimeout(resolve, TAKEOVER_SETTLE_MS))
    },
  }
}

type LeaderRecord = { tabId: string; lastSeen: number }

function readLeader(): LeaderRecord | null {
  const raw = localStorage.getItem(LEADER_STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as LeaderRecord
  } catch {
    return null
  }
}

function writeLeader(tabId: string) {
  localStorage.setItem(LEADER_STORAGE_KEY, JSON.stringify({ tabId, lastSeen: Date.now() }))
}

function isLeaderTab(tabId: string): boolean {
  const leader = readLeader()
  const now = Date.now()
  if (!leader || now - leader.lastSeen > LEADER_STALE_MS) {
    writeLeader(tabId)
  }
  return readLeader()?.tabId === tabId
}

function createHeartbeatCoordinator() {
  let phase: SessionPhase = 'idle'
  let snapshot: SessionSnapshot = snapshotFromPhase('idle')
  const listeners = new Set<SessionListener>()
  let subscriberCount = 0
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  const tabId = getOrCreateTabId()

  const emit = () => listeners.forEach((listener) => listener())

  const applyPhase = (next: SessionPhase) => {
    phase = next
    const nextSnapshot = snapshotFromPhase(next)
    if (snapshotsEqual(snapshot, nextSnapshot)) return
    snapshot = nextSnapshot
    emit()
  }

  const heartbeat = () => {
    if (isLeaderTab(tabId)) {
      writeLeader(tabId)
      applyPhase('active')
    } else if (phase !== 'blocked') {
      applyPhase('blocked')
    }
  }

  const channel = getBroadcastChannel()
  channel.addEventListener('message', (event: MessageEvent<{ type: string; tabId?: string }>) => {
    if (event.data?.type === 'takeover') {
      const leader = readLeader()
      if (leader?.tabId === tabId) {
        localStorage.removeItem(LEADER_STORAGE_KEY)
      }
      heartbeat()
    } else if (event.data?.type === 'ping' && event.data.tabId !== tabId) {
      heartbeat()
    }
  })

  const handleStorage = (event: StorageEvent) => {
    if (event.key === LEADER_STORAGE_KEY || event.key === TAKEOVER_STORAGE_KEY) {
      heartbeat()
    }
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener: SessionListener) {
      listeners.add(listener)
      subscriberCount += 1

      if (!heartbeatTimer) {
        channel.postMessage({ type: 'ping', tabId })
        heartbeat()
        heartbeatTimer = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS)
        window.addEventListener('storage', handleStorage)
      } else if (phase === 'blocked') {
        // Keep blocked UI; background heartbeat continues
      }

      listener()

      return () => {
        listeners.delete(listener)
        subscriberCount -= 1
        if (subscriberCount === 0 && heartbeatTimer) {
          clearInterval(heartbeatTimer)
          heartbeatTimer = null
          window.removeEventListener('storage', handleStorage)
          const leader = readLeader()
          if (leader?.tabId === tabId) {
            localStorage.removeItem(LEADER_STORAGE_KEY)
          }
          phase = 'idle'
          snapshot = snapshotFromPhase('idle')
        }
      }
    },
    async requestTakeover() {
      localStorage.setItem(TAKEOVER_STORAGE_KEY, String(Date.now()))
      localStorage.removeItem(LEADER_STORAGE_KEY)
      channel.postMessage({ type: 'takeover' })
      writeLeader(tabId)
      await new Promise((resolve) => setTimeout(resolve, TAKEOVER_SETTLE_MS))
    },
  }
}

const coordinator = supportsWebLocks() ? createWebLocksCoordinator() : createHeartbeatCoordinator()

function subscribeToCoordinator(onStoreChange: () => void) {
  return coordinator.subscribe(onStoreChange)
}

function subscribeNoop(_onStoreChange: () => void) {
  return () => {}
}

function getCoordinatorSnapshot(): SessionSnapshot {
  return coordinator.getSnapshot()
}

/** Ask other tabs to release the session lock before starting a forced re-login. */
export function requestSessionTakeover(): Promise<void> {
  return coordinator.requestTakeover()
}

/**
 * Ensures only one browser tab holds an active logged-in session at a time.
 */
export function useSingleTabSession(enabled: boolean) {
  const snapshot = useSyncExternalStore(
    enabled ? subscribeToCoordinator : subscribeNoop,
    enabled ? getCoordinatorSnapshot : () => DISABLED_SNAPSHOT,
    () => DISABLED_SNAPSHOT
  )

  return {
    isBlocked: snapshot.isBlocked,
    isSessionHolder: snapshot.isReady && !snapshot.isBlocked,
    isChecking: enabled && !snapshot.isReady,
    isSuperseded: snapshot.supersededByTakeover,
  }
}
